import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import AIAnswerBlock from "../../components/feature/sections/AIAnswerBlock";
import ProcessSection from "../../components/feature/sections/ProcessSection";
import WhyChooseUs from "../../components/feature/sections/WhyChooseUs";
import ServiceAreasSection from "../../components/feature/sections/ServiceAreasSection";
import TestimonialsSection from "../../components/feature/sections/TestimonialsSection";
import FAQSection from "../../components/feature/sections/FAQSection";
import FinalCTA from "../../components/feature/sections/FinalCTA";

const coreServices = [
  {
    icon: "ri-tools-line",
    title: "Roof Repair",
    description: "Fast, accurate repairs for leaks, damaged shingles, storm damage, and more.",
  },
  {
    icon: "ri-building-line",
    title: "Roof Replacement",
    description: "Complete roof replacement using premium materials designed for Colorado weather.",
  },
  {
    icon: "ri-search-eye-line",
    title: "Roof Inspections",
    description: "Thorough inspections to identify issues early and protect your home's value.",
  },
  {
    icon: "ri-thunderstorms-line",
    title: "Storm Damage Repair",
    description: "Emergency response and restoration after hail, wind, and severe weather events.",
  },
];

const aboutFAQs = [
  {
    question: "Why choose a local roofing company?",
    answer: "Local companies understand regional weather conditions, building requirements, and provide faster service. They are more accountable to their community and reputation, ensuring you receive honest, high-quality work every time.",
  },
  {
    question: "How long has Thomas Roofing & Repair been in business?",
    answer: "Thomas Roofing & Repair has been serving Colorado Springs homeowners with professional, honest roofing services for years, building a strong reputation through quality work and customer satisfaction.",
  },
  {
    question: "Are you licensed and insured?",
    answer: "Yes. We are fully licensed and insured in Colorado. Your property and our crew are protected on every single job we complete.",
  },
  {
    question: "Do you offer free inspections?",
    answer: "Absolutely. We offer completely free roof inspections with no obligation. We believe in giving homeowners honest information before asking for any commitment.",
  },
];

const processSteps = [
  {
    number: "01",
    icon: "ri-heart-line",
    title: "Customer First",
    description: "Every interaction starts with listening to you. We understand your concern before proposing any solution.",
  },
  {
    number: "02",
    icon: "ri-search-eye-line",
    title: "Honest Assessment",
    description: "We provide a thorough, unbiased evaluation. No inflated damage reports, no unnecessary upsells.",
  },
  {
    number: "03",
    icon: "ri-hammer-line",
    title: "Expert Craftsmanship",
    description: "Our skilled team delivers quality work using premium materials — done right the first time.",
  },
  {
    number: "04",
    icon: "ri-thumb-up-line",
    title: "Your Satisfaction",
    description: "We don't consider a job done until you're fully satisfied. We stand behind every project we complete.",
  },
];

export default function AboutPage() {
  return (
    <div className="font-body">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-top"
            style={{
              backgroundImage: `url(https://public.readdy.ai/ai/img_res/3792cf90ef5a42822a29d5cb2b75ca7e.jpg)`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/65 to-black/40" />
          <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-32 pt-40">
            <div className="max-w-3xl">
              <nav className="flex items-center gap-2 mb-6 text-xs text-white/50 font-body">
                <Link to="/" className="hover:text-white/80 cursor-pointer whitespace-nowrap transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-white/70">About Us</span>
              </nav>
              <div className="inline-flex items-center gap-2 bg-[#10B981]/20 border border-[#10B981]/40 rounded-full px-4 py-1.5 mb-8">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-heart-fill text-[#10B981] text-xs"></i>
                </div>
                <span className="text-[#10B981] font-body font-600 text-xs tracking-wider">Colorado Springs Local</span>
              </div>
              <h1 className="font-headline font-900 text-white text-4xl lg:text-5xl xl:text-6xl leading-tight mb-6">
                A Roofing Company Built on{" "}
                <span className="text-[#10B981]">Trust, Quality, and Reliability</span>
              </h1>
              <p className="text-white/80 font-body text-lg leading-relaxed mb-8 max-w-2xl">
                We're not just roofers — we're your neighbors. Thomas Roofing & Repair has built its reputation one honest job at a time in Colorado Springs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/#contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-blue-700 text-white font-body font-700 text-base px-8 py-4 rounded-md cursor-pointer whitespace-nowrap transition-colors"
                >
                  Work With Our Team
                </Link>
                <a
                  href="tel:+17195550100"
                  className="inline-flex items-center justify-center gap-2 bg-white/15 hover:bg-white/25 border border-white/30 text-white font-body font-600 text-base px-8 py-4 rounded-md cursor-pointer whitespace-nowrap transition-colors"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-fill text-[#10B981] text-sm"></i>
                  </div>
                  Call (719) 555-0100
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="bg-white py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-[#10B981] font-body font-600 text-sm tracking-widest uppercase mb-3 block">
                Our Expertise
              </span>
              <h2 className="font-headline font-800 text-[#1A1A1A] text-3xl lg:text-4xl xl:text-5xl mb-4 leading-tight">
                What We Specialize In
              </h2>
              <p className="text-[#6B7280] font-body text-base lg:text-lg max-w-2xl mx-auto">
                Our services are focused on what Colorado Springs homeowners need most — reliable protection from the elements.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {coreServices.map((svc) => (
                <div key={svc.title} className="p-6 rounded-lg border border-gray-100 hover:border-[#10B981]/40 transition-all duration-200 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#10B981]/10 mx-auto mb-4">
                    <i className={`${svc.icon} text-[#10B981] text-xl`}></i>
                  </div>
                  <h3 className="font-headline font-700 text-[#1A1A1A] text-lg mb-2">{svc.title}</h3>
                  <p className="text-[#6B7280] font-body text-sm leading-relaxed">{svc.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <AIAnswerBlock
          heading="About Thomas Roofing & Repair"
          intro="Thomas Roofing & Repair is a local roofing company focused on delivering honest service, reliable workmanship, and long-term protection for homeowners. We believe every customer deserves straight answers, fair pricing, and quality work — every time."
          items={[
            { question: "Our Mission", answer: "To protect Colorado Springs homes with honest assessments, premium materials, and skilled craftsmanship — delivered by a team that genuinely cares." },
            { question: "Our Values", answer: "Integrity, transparency, and quality aren't just words to us. They guide every inspection, estimate, and repair we complete." },
            { question: "Our Community", answer: "We live and work in Colorado Springs. When we do great work, our neighbors notice — and that accountability drives everything we do." },
          ]}
        />

        <ProcessSection
          heading="How We Approach Every Job"
          subtitle="Our customer-first process ensures you get the best outcome — every time."
          steps={processSteps}
        />

        <WhyChooseUs
          heading="A Roofing Company You Can Actually Trust"
          subtitle="We've built our reputation through consistent, honest, high-quality work across Colorado Springs and surrounding communities."
        />

        <ServiceAreasSection
          heading="Serving Colorado Springs & Nearby Communities"
          subheading="We're proud to serve homeowners throughout the Colorado Springs metro and surrounding areas."
          geoCopy="As a locally rooted business, we understand the unique roofing challenges Colorado weather presents. Our team provides fast, community-focused roofing services across El Paso County."
        />

        <TestimonialsSection heading="Trusted by Colorado Springs Homeowners" />

        <FAQSection
          heading="Questions About Working With Us"
          items={aboutFAQs}
        />

        <FinalCTA
          heading="Work With a Roofing Company You Can Trust"
          subtitle="No pressure, no upsells — just honest roofing expertise for Colorado Springs homeowners."
        />
      </main>
      <Footer />
    </div>
  );
}
